import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, CircularProgress, TextField,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import { useTranslation } from 'react-i18next';
import { useProjectInitialization } from '../../../api/hooks/useProjectInitialization';
import { useProject } from '../../../api/hooks/useProjects';
import { useUpsertReifegrad, computeMaturityFromForm } from '../../../api/hooks/useReifegrad';
import { useAutosave } from '../../../hooks/useAutosave';
import { PmCard, PmInfoBox } from '../../../components/pm';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildZeitenReport } from '../reports/zeitenReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';

interface FormState {
  zeiten_budget: string;
  zeiten_start_date: string | null;
  zeiten_end_date: string | null;
  zeiten_pflichttermine: string;
  zeiten_beschraenkungen: string;
}

const EMPTY: FormState = {
  zeiten_budget: '',
  zeiten_start_date: null, zeiten_end_date: null,
  zeiten_pflichttermine: '', zeiten_beschraenkungen: '',
};

/**
 * Schritt 5 — Zeiten. Live-Speichern (Autosave). Flexibilität/Aufwand wurden
 * aus der UI entfernt (Besprechung 2026-06-03); die Spalten bleiben im
 * Backend unangetastet. Budget wurde als Freitext wieder aufgenommen (P-05,
 * Backend-Spalte zeiten_budget ist jetzt text statt numeric) und steht ganz
 * oben im Formular. Bindet GET/PATCH /projects/:id/initialization.
 */
export function ZeitenTab({ projectId, embedded = false }: { projectId: string; embedded?: boolean }) {
  const { t } = useTranslation('projects');
  const { data, isLoading, save } = useProjectInitialization(projectId);
  const upsertReifegrad = useUpsertReifegrad(projectId);
  const { data: projectResp } = useProject(projectId);
  const projectName =
    (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';

  const [form, setForm] = useState<FormState>(EMPTY);

  const autosave = useAutosave<FormState>({
    onSave: async (f) => {
      // Nur die noch sichtbaren Felder patchen.
      await save({
        zeiten_budget: f.zeiten_budget,
        zeiten_start_date: f.zeiten_start_date,
        zeiten_end_date: f.zeiten_end_date,
        zeiten_pflichttermine: f.zeiten_pflichttermine,
        zeiten_beschraenkungen: f.zeiten_beschraenkungen,
      });
      const filled = [
        !!f.zeiten_start_date,
        !!f.zeiten_end_date,
        f.zeiten_pflichttermine.trim().length > 0,
        f.zeiten_beschraenkungen.trim().length > 0,
      ];
      const level = computeMaturityFromForm(filled, [true, true, false, false]);
      try { await upsertReifegrad.mutateAsync({ schritt: 5, data: { reifegrad: level } }); } catch { /* best-effort */ }
    },
  });

  useEffect(() => {
    if (autosave.isDirty) return;
    setForm({
      zeiten_budget: data.zeiten_budget ?? '',
      zeiten_start_date: data.zeiten_start_date ?? null,
      zeiten_end_date: data.zeiten_end_date ?? null,
      zeiten_pflichttermine: data.zeiten_pflichttermine ?? '',
      zeiten_beschraenkungen: data.zeiten_beschraenkungen ?? '',
    });
  }, [
    autosave.isDirty,
    data.zeiten_budget,
    data.zeiten_start_date, data.zeiten_end_date,
    data.zeiten_pflichttermine, data.zeiten_beschraenkungen,
  ]);

  const update = (patch: Partial<FormState>) => {
    const next = { ...form, ...patch };
    setForm(next);
    autosave.schedule(next);
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  const startVal: Dayjs | null = form.zeiten_start_date ? dayjs(form.zeiten_start_date) : null;
  const endVal: Dayjs | null = form.zeiten_end_date ? dayjs(form.zeiten_end_date) : null;
  const grid2 = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 };

  // Start/Ende sind Pflichtfelder (P-04) — rein visuelle FE-Validierung. Die
  // Autosave-Logik bleibt unangetastet: fehlende Pflichtangaben blockieren NICHT
  // das Speichern der übrigen Felder (kein Datenverlust durch ein hartes Gate).
  const startMissing = !form.zeiten_start_date;
  const endMissing = !form.zeiten_end_date;
  const datesIncomplete = startMissing || endMissing;

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('zeiten.title', 'Zeiten') };
  const buildReport = () =>
    buildZeitenReport({
      zeitenStart: data.zeiten_start_date ?? null,
      zeitenEnde: data.zeiten_end_date ?? null,
      zeitenPflichttermine: data.zeiten_pflichttermine ?? '',
      zeitenBeschraenkungen: data.zeiten_beschraenkungen ?? '',
    });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>{t('zeiten.title', 'Zeiten')}</Typography>
            <Typography color="text.secondary">
              {t('zeiten.description', 'Schritt 5 — Pflichttermine und Beschränkungen.')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {!embedded && <PdfExportButton meta={pdfMeta} build={buildReport} />}
            <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} />
          </Stack>
        </Stack>

        <PmCard>
          <Stack spacing={2.5}>
            {datesIncomplete && (
              <PmInfoBox variant="warn" title={t('zeiten.datesRequiredTitle', 'Pflichtangabe fehlt')}>
                {t(
                  'zeiten.datesRequiredHint',
                  'Geplanter Start und geplantes Ende sind Pflichtfelder — bitte beide Daten eintragen.',
                )}
              </PmInfoBox>
            )}
            <Box sx={grid2}>
              <DatePicker
                label={t('zeiten.startDate', 'Frühester Projektstart')}
                value={startVal}
                format="DD.MM.YYYY"
                onChange={(v) => {
                  if (v && !v.isValid()) return;
                  update({ zeiten_start_date: v ? v.format('YYYY-MM-DD') : null });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: startMissing,
                    helperText: startMissing ? t('zeiten.startDateRequired', 'Pflichtfeld') : undefined,
                  },
                }}
              />
              <DatePicker
                label={t('zeiten.endDate', 'Spätestes Projektende')}
                value={endVal}
                format="DD.MM.YYYY"
                onChange={(v) => {
                  if (v && !v.isValid()) return;
                  update({ zeiten_end_date: v ? v.format('YYYY-MM-DD') : null });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: endMissing,
                    helperText: endMissing ? t('zeiten.endDateRequired', 'Pflichtfeld') : undefined,
                  },
                }}
              />
            </Box>

            <TextField
              label={t('zeiten.pflichttermine', 'Pflichttermine')}
              helperText={t('zeiten.pflichttermineHelper', 'z.B. Messe XY am 15.09., Quartalsabschluss')}
              value={form.zeiten_pflichttermine}
              onChange={(e) => update({ zeiten_pflichttermine: e.target.value })}
              multiline rows={3} fullWidth
            />
            <TextField
              label={t('zeiten.budget', 'Budget')}
              helperText={t('zeiten.budgetHelper', 'Optional / Freitext')}
              value={form.zeiten_budget}
              onChange={(e) => update({ zeiten_budget: e.target.value })}
              multiline rows={2} fullWidth
            />
            <TextField
              label={t('zeiten.beschraenkungen', 'Beschränkungen')}
              helperText={t('zeiten.beschraenkungenHelper', 'Urlaubszeiten, Betriebsferien, Abhängigkeiten')}
              value={form.zeiten_beschraenkungen}
              onChange={(e) => update({ zeiten_beschraenkungen: e.target.value })}
              multiline rows={2} fullWidth
            />
          </Stack>
        </PmCard>
      </Box>
    </LocalizationProvider>
  );
}
